import { Loader } from "@googlemaps/js-api-loader";
import { MarkerClusterer } from "@googlemaps/markerclusterer";

/**
 * @typedef Location
 * @type {Object}
 * @property {number} lat Location latitude
 * @property {number} lng Location latitude
 * @property {string} id Location ID
 * @property {string} category Location Category, has to match with filter ID
 * @property {string} info Location label
 * @property {string} pin Pin image URL
 * @property {Array.<number>} pinSize Pin size array
 * @property {string} icon Icon image URL
 * @property {string} thumbnail Thumbnail image URL
 */

export default class ShrMapFactory {
  /**
   * constructor description
   * @param {object} config Config object containing mobile and desktop config
   * @param {object} config.mobile Config object containing mobile config
   * @param {object} config.desktop Config object containing desktop config
   * @param {string} config.apiKey Config for API key
   * @param {HTMLElement} mapEl HTML element of map wrapper
   * @param {object} markersData Object containing markers data
   */
  constructor(mapEl, config, markersData, mode = 'global') {
    this.loader = new Loader({
      apiKey: config.apiKey,
      version: "weekly",
    });

    this.config = config;
    this.markersData = markersData;
    this.mapEl = mapEl;
    this.infoWindow = null;
    this.map = null;
    this.markers = null;
    this.cluster = null;
    this.googleMaps = {};
    this.mode = mode;
  }

  async init() {
    await this.initMap();
  }

  async initMap() {
    const { Map, InfoWindow } = await this.loader.importLibrary("maps");
    const { Size } = await this.loader.importLibrary("core");
    const { Marker, AdvancedMarkerElement } = await this.loader.importLibrary("marker");

    this.googleMaps = { Map, InfoWindow, Size, Marker, AdvancedMarkerElement };

    const config = this.getConfig();
    if (!config.mapId) {
      console.error('No mapId provided setting default mapId');
      config.mapId = "4504f8b37365c3d0";
    }


    this.map = new Map(this.mapEl, config);

    if (this.config.style) {
      this.map.set("styles", this.config.style);
    }

    const markersData = typeof this.markersData === "string" ? JSON.parse(this.markersData) : this.markersData;

    this.infoWindow = new InfoWindow({
      content: "",
      disableAutoPan: true,
    });

    await this.createMarkers(markersData, this.map, this.infoWindow);
  }

  getConfig() {
    if (window.matchMedia("(min-width: 1024px)").matches) {
      return this.config.desktop;
    }
    return this.config.mobile;
  }

  async createMarkers(markersData, map) {
    Object.entries(markersData).forEach((item) => {
      const value = item[1];
      const clusterIcon = value[0].clusterIcon ?? null;

      if (Array.isArray(value)) {
        this.markers = value.map((/** Location */ location) => {
          let marker;
          if (this.mode === 'global') {
            const markerHtml = document.createElement("div");
            markerHtml.classList.add("custom-marker");
            // add icon to marker
            const markerIcon = document.createElement("img");
            markerIcon.src = location.pin;
            markerIcon.style.width = "40px";
            markerIcon.style.height = "40px";
            markerIcon.classList.add("custom-marker-icon");
            markerHtml.appendChild(markerIcon);
            // add label to marker

            var markerLabelBackgroundColor = `#${location.hexColour}`;
            if (location.hexColour == undefined || location.hexColour == ''){
              markerLabelBackgroundColor = '#00a5b5'
            }
            
            const markerLabel = document.createElement("div");
            markerLabel.innerHTML = location.info;
            markerLabel.classList.add("custom-marker-label");
            markerLabel.style.backgroundColor = markerLabelBackgroundColor;

            // add link to marker label
            const markerLink = document.createElement("a");
            markerLink.href = location.link;


            const icon = document.createElement("img");
            icon.src = "/assets/icons/chevron-circle-next.svg";
            icon.classList.add("custom-marker-icon-link");
            markerLink.appendChild(icon);
            markerLabel.appendChild(markerLink);



            markerHtml.appendChild(markerLabel);

            // popup content
            const popupContent = document.createElement("div");
            popupContent.classList.add("custom-marker-popup");
            popupContent.innerHTML = `
            <div class="custom-marker-popup-content">
              <img src="/assets/icons/close.svg" class="close-icon" />
              <img src="${location.thumbnail}" alt="${location.info}" class="custom-marker-popup-thumbnail" />
            </div>`;
            markerHtml.appendChild(popupContent);




            const advancedMarkerOptions = {
              map,
              position: { lat: location.lat, lng: location.lng },
              content: markerHtml
            };

            marker = new this.googleMaps.AdvancedMarkerElement(advancedMarkerOptions);
            marker.addListener("click", (e) => {

              if (e.domEvent.target.classList.contains("close-icon")) {
                markerHtml.classList.remove("active");
                return;
              }

              if (!markerHtml.classList.contains("active")) {
                markerHtml.classList.add("active");
              }
            });

          }
          else if (this.mode === 'filtrable') {
            const pinSize = location.pinSize
              ? new this.googleMaps.Size(location.pinSize[0], location.pinSize[1])
              : new this.googleMaps.Size(40, 40);

            const markerOptions = {
              map,
              position: { lat: location.lat, lng: location.lng },
              draggable: false,
              icon: {
                url: location.pin ?? "",
                scaledSize: pinSize,
              },
            };
            marker = new this.googleMaps.Marker(markerOptions);
            marker.set("id", location.id);
            marker.set("category", location.category);

            marker.addListener("click", () => {
              this.setInfoWindowContent({
                thumbnail: location.thumbnail ?? "",
                title: location.info ?? "",
                link: location.link ?? "#",
              });
              this.infoWindow.open(map, marker);
              map.panTo(marker.getPosition());
              map.panBy(0, -100);
            });

          }
          return marker;
        });

        this.createCluster(clusterIcon);
      }
    });
  }



  /**
   *
   * @param {Object[]} categories
   */
  toggleMarkers(categories = null) {
    const visibleMarkers = this.markers.filter((marker) => categories.includes(marker.get("category")));

    this.markers.map((marker) => {
      if (categories.includes(marker.get("category"))) {
        marker.setVisible(true);
      } else {
        marker.setVisible(false);
      }
    });

    /* Re-set cluster markers and redraw the map */
    this.cluster.markers = visibleMarkers;
    // eslint-disable-next-line no-undef
    google.maps.event.trigger(this.map, "idle");
  }

  createCluster(clusterIcon) {
    // Generate marker clusters per region
    this.cluster = new MarkerClusterer({
      map: this.map,
      markers: this.markers,
      renderer: {
        render: ({ markers, _position: position }) =>
          new this.googleMaps.Marker({
            position: {
              lat: position.lat(),
              lng: position.lng(),
            },
            label: {
              text: String(markers.length),
              color: "white",
            },
            icon: clusterIcon ?? null,
          }),
      },
    });
  }

  /**
   * Pan map to location
   * @param {Location} location
   * @param {number | null | undefined} zoom
   * @param {boolean | null | undefined} openInfoWindow
   */
  centerMapToMarker(location, zoom = null, openInfoWindow = false) {
    this.map.panTo({ lng: location.lng, lat: location.lat });
    if (zoom) {
      this.map.setZoom(zoom);
    }
    if (openInfoWindow) {
      const marker = this.markers.filter((m) => m.getPosition().lat() === location.lat && m.getPosition().lng() === location.lng)[0];
      this.setInfoWindowContent({
        thumbnail: location.thumbnail ?? "",
        title: location.info ?? "",
        link: location.link ?? "#",
      });
      this.infoWindow.open(this.map, marker);
      this.map.panBy(0, -100);
    }
  }

  /**
   * Zoom map
   * @param {number | null | undefined} zoom
   */
  zoomTo(zoom) {
    if (zoom) {
      this.map.setZoom(zoom);
    }
  }

  /**
   * Set info window content
   * @param {string} thumbnail
   * @param {string} title
   * @param {string} link
   */
  setInfoWindowContent({ thumbnail, title, link }) {
    this.infoWindow.setContent(`
                                <div style="width: 230px; min-height: 171px">
                              <a href="${link ?? "#"}"
                                ><img
                                  style="width: 100%; height: 100%; max-height: 128px; object-fit: cover"
                                  src="${thumbnail}"
                                />
                                <div class="map-infowindow-content-wrapper">
                                <span
                                  class="map-infowindow-title"
                                >
                                  ${title}
                                </span>
                                </div>
                              </a>
                            </div>
                            `);
  }
}
